
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { TutorialCard } from '@/components/tutorials/TutorialCard';
import { Button } from '@/components/ui/button';
import { useCategories, useCategoryTutorials } from '@/hooks/useCategories';

export default function CategoryDetailPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { data: categories = [] } = useCategories();
  const { data: tutorials = [], isLoading } = useCategoryTutorials(categoryId);
  
  const category = categories.find((c: any) => c.id === categoryId);

  if (isLoading) {
    return (
      <MainLayout>
        <div className="container py-16 px-4 text-center">
          <div className="animate-pulse">Loading tutorials...</div>
        </div>
      </MainLayout>
    );
  }
  
  if (!category) {
    return (
      <MainLayout>
        <div className="container py-16 px-4 text-center">
          <h1 className="text-3xl font-bold mb-4">Category Not Found</h1>
          <p className="mb-8">The category you're looking for doesn't exist.</p>
          <Link to="/categories">
            <Button>Browse All Categories</Button>
          </Link>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout showSidebar>
      <div className="container py-16 px-4">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Link to="/categories" className="hover:text-primary">Categories</Link>
            <span>/</span>
            <span>{category.name}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{category.name}</h1>
          <p className="text-lg text-muted-foreground">{category.description}</p>
        </div>
        
        {tutorials.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tutorials.map((tutorial: any) => (
              <TutorialCard key={tutorial.id} tutorial={tutorial} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <h3 className="text-lg font-medium">No tutorials in this category yet</h3>
            <p className="text-muted-foreground mt-2">
              Check back later for new content or browse other categories
            </p>
            <Link to="/categories">
              <Button variant="outline" className="mt-4">
                Browse All Categories
              </Button>
            </Link>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
